import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { User, Notification } from "@/types";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import DashboardStats from "@/components/admin/dashboard/DashboardStats";
import UserManagement from "@/components/admin/UserManagement";
import ProductManagement from "@/components/admin/ProductManagement";
import NotificationsList from "@/components/admin/dashboard/NotificationsList";
import NotificationSettings from "@/components/admin/NotificationSettings";

export default function AdminDashboard() {
  // État pour stocker la valeur de l'onglet actif
  const [activeTab, setActiveTab] = useState("users");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    activeProducts: 0,
    expiredProducts: 0
  });

  useEffect(() => {
    fetchStats();
    fetchNotifications();
    
    // Set up real-time subscription for stats updates
    const channel = supabase
      .channel('dashboard-stats-updates')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'user_profiles' }, 
        (payload) => {
          console.log('Stats update triggered by user_profiles change:', payload);
          fetchStats();
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'products' }, 
        (payload) => {
          console.log('Stats update triggered by products change:', payload);
          fetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchStats = async () => {
    try {
      console.log('Fetching dashboard stats...');
      
      // Fetch user stats
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('role, is_active');
      
      if (usersError) throw usersError;

      console.log('Users data for stats:', users);

      const totalUsers = users ? users.length : 0;
      const totalAdmins = users ? users.filter(user => user.role === 'admin').length : 0;

      console.log(`Total users: ${totalUsers}, Total admins: ${totalAdmins}`);

      // Fetch product stats
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('status');
      
      if (productsError) throw productsError;

      const activeProducts = products ? products.filter(product => product.status === 'active').length : 0;
      const expiredProducts = products ? products.filter(product => product.status === 'expired').length : 0;

      setStats({
        totalUsers,
        totalAdmins,
        activeProducts,
        expiredProducts
      });

      console.log('Stats updated:', { totalUsers, totalAdmins, activeProducts, expiredProducts });
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
      console.error('Error fetching stats:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const { data: emailLogs, error } = await supabase
        .from('email_logs')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      const notifications = emailLogs ? emailLogs.map(log => ({
        id: log.id,
        userId: '',
        message: `Email sent to ${log.recipient_email}: ${log.subject}`,
        isRead: true,
        createdAt: log.sent_at || ''
      })) : [];

      setNotifications(notifications);
    } catch (error) {
      toast.error("Erreur lors du chargement des notifications");
      console.error('Error:', error);
    }
  };

  const handleMarkNotificationAsRead = (notificationId: string) => {
    setNotifications(notifications.map(notif =>
      notif.id === notificationId ? { ...notif, isRead: true } : notif
    ));
  };

  // Fonction pour changer l'onglet actif
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <main className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-6">Tableau de Bord Administrateur</h1>
        
        <DashboardStats 
          totalUsers={stats.totalUsers}
          totalAdmins={stats.totalAdmins}
          activeProducts={stats.activeProducts}
          expiredProducts={stats.expiredProducts}
        />
        
        <Tabs 
          value={activeTab} 
          onValueChange={handleTabChange} 
          className="mb-8"
        >
          <TabsList className="w-full max-w-2xl mx-auto mb-6">
            <TabsTrigger value="users" className="flex-1">Utilisateurs</TabsTrigger>
            <TabsTrigger value="products" className="flex-1">Produits</TabsTrigger>
            <TabsTrigger value="notifications" className="flex-1">Notifications</TabsTrigger>
            <TabsTrigger value="settings" className="flex-1">Paramètres</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>
          
          <TabsContent value="products">
            <ProductManagement />
          </TabsContent>
          
          <TabsContent value="notifications">
            <Card>
              <NotificationsList 
                notifications={notifications}
                onMarkAsRead={handleMarkNotificationAsRead}
              />
            </Card>
          </TabsContent>
          
          <TabsContent value="settings">
            <NotificationSettings />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
